// Embedded entry point - minimal bootstrap
// Detects anchor type and loads appropriate code
// HIDDEN-only deployments: ~500 bytes (no visual anchor code)
// Visual anchors: lazy loads visual-anchors-entry.jsx (~1KB)

import { HiddenAnchor } from './vanilla/hidden-anchor';

/**
 * Load merchant configuration from API (non-blocking)
 * Always fetches from API to get full configuration
 * Runs in background to not delay UI initialization
 */
function loadMerchantConfigurationAsync() {
  // Get merchant key from available sources
  const merchantKey = getMerchantKeySync();
  if (!merchantKey) {
    return Promise.resolve();
  }

  // Lazy load ConfigurationService to keep initial bundle small
  return import(
    /* webpackChunkName: "config-service" */
    './Services/ConfigurationService'
  ).then(module => {
    const ConfigurationService = module.default;
    return ConfigurationService.initializeCompatioConfig(merchantKey);
  }).catch(error => {
    console.error('[ISR] Failed to load merchant configuration:', error);
    // Continue without API config - app will use existing window.compatioConfig if any
  });
}

/**
 * Synchronous merchant key lookup (no heavy imports)
 */
function getMerchantKeySync() {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search);
  const merchantKeyFromUrl = urlParams.get('merchantKey');
  if (merchantKeyFromUrl) return merchantKeyFromUrl;

  // Check existing window.compatioConfig
  const compatioConfig = window.compatioConfig;
  if (compatioConfig?.merchantKey) return compatioConfig.merchantKey;
  if (compatioConfig?.custom?.merchantKey) return compatioConfig.custom.merchantKey;

  // Check localStorage as fallback
  try {
    const storedKey = localStorage.getItem('compatioMerchantKey');
    if (storedKey) return storedKey;
  } catch (e) {
    // localStorage might be blocked
  }

  return null;
}

// Initialize embedded mode with minimal footprint
export function initEmbedded() {
  // Start loading merchant configuration in background (non-blocking)
  // Config will be cached and available when Guide builder opens
  const configPromise = loadMerchantConfigurationAsync();

  // Store config promise globally so HiddenAnchor can wait for it
  window.__compatioConfigPromise = configPromise;

  // Check for HIDDEN container first (most common for embedded)
  const hiddenContainer = document.querySelector('.compatio-builder-container');
  if (hiddenContainer) {
    // Initialize vanilla JS hidden anchor (bundled in embedded chunk)
    // Pass config promise so it can wait before opening builder
    new HiddenAnchor(configPromise);
  }

  // Check if ANY visual anchors exist on the page
  const hasVisualAnchors =
    document.querySelector('#sb3-container') ||
    document.querySelector('#compatio-sb3-button') ||
    document.querySelector('[compatio-builder]');

  // Only load visual anchor code if visual containers detected
  // This prevents loading ~1KB of Preact/CSS code for HIDDEN-only deployments
  if (hasVisualAnchors) {
    import(
      /* webpackChunkName: "visual-anchors" */
      './visual-anchors-entry'
    ).then(module => {
      module.initVisualAnchors();

      // Store reference for SPA re-initialization
      window.__compatioVisualAnchors = module;
    }).catch(error => {
      console.error('[ISR] Failed to load visual anchors:', error);
    });
  }

  // Return config promise for callers who need to wait
  return configPromise;
}

// Listen for SPA page change events (for React/Vue/Angular apps)
window.addEventListener('compatioPageChange', () => {
  // Small delay to allow DOM to update
  setTimeout(() => {
    // Check if visual anchors module is loaded
    if (window.__compatioVisualAnchors) {
      window.__compatioVisualAnchors.initVisualAnchors();
    } else {
      // Full re-initialization if needed
      initEmbedded();
    }
  }, 100);
});

// Auto-initialize on load (synchronous - doesn't wait for config API)
// Config loads in background and will be cached when Guide opens
initEmbedded();

// Mark widget as ready immediately (UI is ready, config loads in background)
window.compatioReady = true;